import { TextField, Typography } from '@mui/material'
import { AutocompleteRenderInputParams, Autocomplete } from '@mui/material'
import { HTMLAttributes, useRef } from 'react'
import type { Ref } from 'react'
import { CaseCategory } from './CaseService'
import MemberFacingCaseAlert from './MemberFacingCaseAlert'
import { useFocusOnOpen } from '~/shared/hooks/useFocusOnOpen'

interface CaseCategoryInputParams extends AutocompleteRenderInputParams {
  category: CaseCategory | null
  freeSolo?: boolean
  inputRef: Ref<HTMLInputElement>
}

interface CaseCategoryOptionParams extends HTMLAttributes<HTMLLIElement> {
  category: CaseCategory
}

interface Props {
  categories: CaseCategory[]
  category: CaseCategory | null
  setCategory: (arg0: CaseCategory | null) => void
  freeSolo?: boolean
  handleAddCase?: (arg0: CaseCategory) => void
  onBlur?: () => void
  disabled?: boolean
}

const CaseCategoryInput = (params: CaseCategoryInputParams) => {
  // freeSolo and category are custom props on params; pull them out so they
  // aren't spread onto the TextField's DOM input.
  const { freeSolo: _freeSolo, category: _category, inputRef, ...textFieldParams } = params
  return (
    <>
      <TextField
        {...textFieldParams}
        inputRef={inputRef}
        placeholder={params.freeSolo ? 'Add case, `esc` to cancel' : 'Case Category'}
        variant="outlined"
        slotProps={{
          // Keep the Autocomplete's own slots — `htmlInput` carries the ref and
          // props for the real <input>, without which useAutocomplete cannot
          // focus it.
          ...params.slotProps,
          input: {
            ...params.slotProps.input,
            endAdornment: (
              <>
                {params.category?.is_member_facing ? (
                  <MemberFacingCaseAlert
                    style={{ fontSize: '0.85em' }}
                    key={String(params.category?.id)}
                    tagKey={String(params.category?.id)}
                  />
                ) : null}
                {params.slotProps.input.endAdornment}
              </>
            ),
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': { padding: params.freeSolo ? 0 : 1 },
        }}
      />
    </>
  )
}

const CaseCategoryOption = (params: CaseCategoryOptionParams) => {
  return (
    <Typography {...params}>
      {params.category.title}
      {params.category?.is_member_facing ? (
        <>
          &nbsp;&nbsp;
          <MemberFacingCaseAlert
            style={{ fontSize: '0.85em' }}
            key={String(params.category?.id)}
            tagKey={String(params.category?.id)}
          />
        </>
      ) : null}
    </Typography>
  )
}

const CaseCategorySelect = ({
  categories,
  category,
  setCategory,
  freeSolo,
  handleAddCase,
  onBlur,
  disabled,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  // Free-solo mode is the "add case" field the user just clicked for, so focus follows that click.
  useFocusOnOpen(inputRef, !!freeSolo)

  return freeSolo ? (
    <Autocomplete<CaseCategory, false, false, true>
      id="grouped-category"
      openOnFocus
      freeSolo
      disabled={disabled}
      onBlur={onBlur}
      onKeyDown={e => {
        if (onBlur && e.key === 'Escape') {
          onBlur()
          e.stopPropagation()
        }
      }}
      options={categories
        .filter(category => !category.is_system_only && !category.is_deprecated)
        .sort((a, b) => -(b.parent || 'UNCATEGORIZED').localeCompare(a.parent || 'UNCATEGORIZED'))}
      groupBy={option => option.parent || 'UNCATEGORIZED'}
      getOptionLabel={option => (typeof option === 'string' ? option : option.title)}
      value={category}
      renderInput={params => (
        <CaseCategoryInput
          {...params}
          category={category}
          freeSolo={freeSolo}
          inputRef={inputRef}
        />
      )}
      renderOption={({ key, ...props }, option) => (
        <CaseCategoryOption key={key} {...props} category={option} />
      )}
      onChange={(_event, newValue) => {
        if (handleAddCase && newValue && typeof newValue !== 'string') handleAddCase(newValue)
      }}
      sx={{ '& .MuiAutocomplete-input': { fontSize: '1.4rem' } }}
      slotProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { fontSize: '1.4rem' } } } }}
    />
  ) : (
    <Autocomplete<CaseCategory>
      id="grouped-category"
      disabled={disabled}
      options={categories
        .filter(category => !category.is_system_only && !category.is_deprecated)
        .sort((a, b) => -(b.parent || 'UNCATEGORIZED').localeCompare(a.parent || 'UNCATEGORIZED'))}
      groupBy={option => option.parent || 'UNCATEGORIZED'}
      getOptionLabel={option => option.title}
      value={category}
      renderInput={params => (
        <CaseCategoryInput {...params} category={category} inputRef={inputRef} />
      )}
      renderOption={({ key, ...props }, option) => (
        <CaseCategoryOption key={key} {...props} category={option} />
      )}
      onChange={(_event, newValue) => {
        if (newValue) setCategory(newValue)
      }}
    />
  )
}

export default CaseCategorySelect
